<script setup lang="ts">
import { useDocumentVisibility } from '@vueuse/core'

const { idledFor } = useInactivityMonitor()

const userStore = useUserStore()
const visibility = useDocumentVisibility()

const showInactivityDialog = ref(false)

// Track when the tab was last made visible, so we can ignore the first
// idledFor spike that arrives before the user's mouse movement resets it
const lastBecameVisible = ref(0)

watch(visibility, (newVal) => {
  if (newVal === 'visible') {
    lastBecameVisible.value = Date.now()
  }
})

const handleClosed = () => {
  showInactivityDialog.value = false
}

// Listen for logout from another tab
const sessionChannel = useSessionChannel()

sessionChannel.onLogout(async () => {
  // Another tab logged out — clean up this tab's state gracefully
  if (!userStore.isAuthenticated || userStore.loggingOut) return

  const { logSessionEvent } = useSessionAudit()
  logSessionEvent('session:logout:cross_tab')

  const { clearCookies, loginRedirect } = useAuth()
  userStore.clearAuth()
  clearCookies()
  await loginRedirect({ redirectQuery: true })

  showInactivityDialog.value = true
})

onUnmounted(() => {
  sessionChannel.destroy()
})

watch(idledFor, async (idledFor) => {
  if (!(idledFor >= 60 * 30)) return // 30 minutes

  // Grace period: if the tab just became visible (within 2 seconds),
  // skip this tick — the user's interaction events haven't had time
  // to reset lastActive yet
  if (Date.now() - lastBecameVisible.value < 2000) return

  const { logSessionEvent } = useSessionAudit()
  logSessionEvent('session:logout:inactivity', { idledForSeconds: idledFor })

  await userStore.logoutUser({ redirectQuery: true })

  showInactivityDialog.value = true
})
</script>

<template>
  <LazyDialog
    v-model="showInactivityDialog"
    @closed="handleClosed"
  >
    <template #header>
      <h2 class="font-bold text-xl self-stretch leading-[26px]">Your session has timed out</h2>
    </template>
    <template #body>
      <p class="text-[16px] text-center self-stretch">
        You have been logged out due to inactivity. Please log in again.
      </p>
    </template>
    <template #actions>
      <UIButton
        class="w-[180px] font-bold text-[16px]"
        color="primary"
        rounded="full"
        @click="handleClosed"
      >
        Done
      </UIButton>
    </template>
  </LazyDialog>
</template>
